import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { cn } from '../../lib/cn'

const ArrowIcon = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg
    width='23'
    height='19'
    viewBox='0 0 23 19'
    fill='none'
    aria-hidden='true'
    className={direction === 'left' ? 'rotate-180' : undefined}
  >
    <path
      d='M22.4297 9.21094C22.4297 9.63281 22.2773 9.99219 21.9727 10.2891L14.2734 17.9648C13.9688 18.2695 13.6289 18.4219 13.2539 18.4219C12.8242 18.4219 12.4766 18.2891 12.2109 18.0234C11.9453 17.7578 11.8125 17.4297 11.8125 17.0391C11.8125 16.8203 11.8516 16.625 11.9297 16.4531C12.0156 16.2812 12.125 16.1289 12.2578 15.9961L14.8828 13.3477L19.043 9.55078L19.582 10.4297L15.3164 10.6758H1.48828C1.04297 10.6758 0.683594 10.543 0.410156 10.2773C0.136719 10.0039 0 9.64844 0 9.21094C0 8.78125 0.136719 8.43359 0.410156 8.16797C0.683594 7.89453 1.04297 7.75781 1.48828 7.75781H15.3164L19.582 7.99219L19.043 8.89453L14.8828 5.07422L12.2578 2.42578C12.125 2.29297 12.0156 2.14063 11.9297 1.96875C11.8516 1.78906 11.8125 1.59375 11.8125 1.38281C11.8125 0.992188 11.9453 0.664063 12.2109 0.398438C12.4766 0.132812 12.8242 0 13.2539 0C13.4336 0 13.6094 0.0390625 13.7812 0.117188C13.9609 0.1875 14.1328 0.304688 14.2969 0.46875L21.9727 8.13281C22.2773 8.42969 22.4297 8.78906 22.4297 9.21094Z'
      fill='currentColor'
    />
  </svg>
)

export interface SubjectCarouselProps {
  children: ReactNode
  /** Optional title rendered on the left of the header row, alongside the nav arrows */
  title?: string
  /** Gap between slides in pixels */
  gap?: number
  /** Max width of the content grid in pixels */
  maxWidth?: number
  /** Accessible label for the carousel region */
  ariaLabel?: string
  /** Accessible label for the previous button */
  prevLabel?: string
  /** Accessible label for the next button */
  nextLabel?: string
  /** Additional class name for the root element */
  className?: string
  /** Left padding for the slide track in pixels */
  paddingLeft?: number
  /** Additional class name applied to the scroll track (use for responsive padding via Tailwind) */
  scrollClassName?: string
}

export const SubjectCarousel = ({
  children,
  title,
  gap = 24,
  maxWidth = 960,
  ariaLabel,
  prevLabel = 'Previous',
  nextLabel = 'Next',
  className,
  paddingLeft,
  scrollClassName,
}: SubjectCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canGoPrev, setCanGoPrev] = useState(false)
  const [canGoNext, setCanGoNext] = useState(false)
  const activeIndexRef = useRef(0)
  const childArray = useMemo(() => React.Children.toArray(children), [children])
  const slideCount = childArray.length

  const syncScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    setCanGoPrev(el.scrollLeft > 0)
    setCanGoNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)

    const elLeft = el.getBoundingClientRect().left
    let closest = 0
    let minDist = Infinity
    Array.from(el.children).forEach((child, i) => {
      const childLeft =
        (child as HTMLElement).getBoundingClientRect().left - elLeft
      const dist = Math.abs(childLeft)
      if (dist < minDist) {
        minDist = dist
        closest = i
      }
    })
    activeIndexRef.current = closest
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return undefined

    let rafId: number | null = null
    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(syncScrollState)
    }

    el.addEventListener('scroll', handleScroll, { passive: true })

    // ResizeObserver handles: initial render, display:none → visible, container resize
    const ro = new ResizeObserver(syncScrollState)
    ro.observe(el)

    return () => {
      el.removeEventListener('scroll', handleScroll)
      if (rafId) cancelAnimationFrame(rafId)
      ro.disconnect()
    }
  }, [syncScrollState])

  const scrollToIndex = (index: number) => {
    const el = scrollRef.current
    if (!el) return
    const child = el.children[index] as HTMLElement | undefined
    if (!child) return
    activeIndexRef.current = index
    const childLeft =
      child.getBoundingClientRect().left -
      el.getBoundingClientRect().left +
      el.scrollLeft
    el.scrollTo({ left: childLeft, behavior: 'smooth' })
  }

  return (
    <div
      className={cn('w-full', className)}
      role='region'
      aria-roledescription='carousel'
      aria-label={ariaLabel}
    >
      <div
        className='mx-auto mb-6 w-full'
        style={{
          maxWidth,
        }}
      >
        {title && (
          <div
            className='mb-2.5 h-px bg-edu-stroke-default'
            aria-hidden='true'
          />
        )}
        <div
          className={cn(
            'flex items-center',
            title ? 'justify-between' : 'justify-end',
          )}
        >
          {title && (
            <h2 className='m-0 text-[20px] leading-[1.4] font-semibold text-white md:text-title-3 md:leading-title-3'>
              {title}
            </h2>
          )}
          <div
            className='flex items-center'
            role='group'
            aria-label={ariaLabel ? `${ariaLabel} navigation` : undefined}
          >
            <button
              type='button'
              onClick={() =>
                canGoPrev && scrollToIndex(activeIndexRef.current - 1)
              }
              aria-disabled={!canGoPrev}
              tabIndex={canGoPrev ? undefined : -1}
              aria-label={prevLabel}
              className={cn(
                'flex h-12 w-12 items-center justify-center text-white transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
                canGoPrev ? 'cursor-pointer' : 'cursor-default opacity-30',
              )}
            >
              <ArrowIcon direction='left' />
            </button>
            <button
              type='button'
              onClick={() =>
                canGoNext && scrollToIndex(activeIndexRef.current + 1)
              }
              aria-disabled={!canGoNext}
              tabIndex={canGoNext ? undefined : -1}
              aria-label={nextLabel}
              className={cn(
                'flex h-12 w-12 items-center justify-center text-white transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
                canGoNext ? 'cursor-pointer' : 'cursor-default opacity-30',
              )}
            >
              <ArrowIcon direction='right' />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className={cn(
          'flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          scrollClassName,
        )}
        style={{
          gap: `${gap}px`,
          paddingLeft:
            scrollClassName == null && paddingLeft != null
              ? `${paddingLeft}px`
              : undefined,
          scrollPaddingLeft: (() => {
            if (scrollClassName != null) return undefined
            if (paddingLeft != null) return `${paddingLeft}px`
            return `calc((100vw - ${maxWidth}px) / 2)`
          })(),
        }}
      >
        {childArray.map((child, i) => (
          <div
            key={(child as React.ReactElement).key}
            className='shrink-0 snap-start'
            role='group'
            aria-roledescription='slide'
            aria-label={`${i + 1} of ${slideCount}`}
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  )
}
